// src/components/AnswerMarkdown.jsx
//
// Renders an LLM-narrated answer (from /proxy/checks/{auto,ask,audit,...}) as a
// styled PM-style briefing rather than raw Markdown text — ## headings get an
// accent banner, ### headings get muted eyebrow labels, lists/bold/code render
// as real elements. Shared by ChecksPage.jsx and AskPage.jsx so both surfaces
// render the same narration consistently.
//
// Deliberately not a general Markdown parser — the source is our own system
// prompt (see conformance/autoRun.js, askTraffic.js), not arbitrary user
// content, so only the small subset those prompts are instructed to emit
// (#/##/### headings, bullet/numbered lists, pipe tables, fenced code, bold and
// code spans, paragraphs) is handled. No Markdown dependency (AC 69).
import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { parseMarkdownBlocks } from '../utils/markdownBlocks';

// WP6 item 2 — the analyst cites evidence rows inline as `[#rowId host path @ts]`
// (see conformance/askAnalyst.js's citation-format contract). `rowId` is the
// 0-based index into whatever `rows` array was posted with that turn.
const ROW_REF_RE = /^\[#(\d+)([^\]]*)\]$/;

function renderInline(text, keyPrefix, onRowRef) {
  const parts = String(text ?? '')
    .split(/(\*\*[^*]+\*\*|`[^`]+`|\[#\d+[^\]]*\])/g)
    .filter((s) => s !== '');
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-b-${i}`}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <Box
          key={`${keyPrefix}-c-${i}`}
          component="code"
          sx={{ fontFamily: 'monospace', fontSize: '0.9em', background: 'action.selected', px: 0.5, borderRadius: 0.5 }}
        >
          {part.slice(1, -1)}
        </Box>
      );
    }
    const rowRef = ROW_REF_RE.exec(part);
    if (rowRef) {
      const rowId = Number(rowRef[1]);
      const label = rowRef[2].trim(); // e.g. "host /path @ts" — shown on hover
      // Graceful degrade: no handler (no scoped rows to resolve against) ->
      // render as plain styled text, not an inert/misleading clickable chip.
      if (!onRowRef) {
        return (
          <Box
            key={`${keyPrefix}-r-${i}`}
            component="span"
            sx={{ fontFamily: 'monospace', fontSize: '0.85em', color: 'text.secondary' }}
          >
            {part}
          </Box>
        );
      }
      return (
        <Tooltip key={`${keyPrefix}-r-${i}`} title={label || `row #${rowId}`} arrow>
          <Chip
            component="button"
            type="button"
            size="small"
            clickable
            label={`#${rowId}`}
            onClick={() => onRowRef(rowId, label)}
            sx={{
              height: 18, fontSize: 10, mx: 0.25, verticalAlign: 'middle',
              fontFamily: 'ui-monospace, Menlo, monospace', cursor: 'pointer',
              bgcolor: 'action.selected', border: 0,
            }}
          />
        </Tooltip>
      );
    }
    return part;
  });
}

// docs/agent-response-format.md §4 — heading-keyed styling. The narration
// prompts (conformance/answerFormat.js's buildScope, autoRun.js, askTraffic.js)
// render a fixed six-heading skeleton; these two headings get light extra
// treatment beyond the generic h2/h3/list rendering below. Exported (pure,
// no React) so they're unit-testable without a DOM/RTL harness.
const SCOPE_HEADING = 'Scope';
const MISSING_HEADING = 'Missing / not verifiable';

// The deterministic Scope line (conformance/answerFormat.js's buildScope) has
// the shape "<N> rows · <window> · types: t1(n) · t2(n) …[· app x][· step y]
// [· source: z]". When a Scope section's body matches that shape, split it on
// " · " into chip-worthy segments; otherwise return null so the caller falls
// back to plain text (e.g. an LLM's free-form rephrasing that doesn't start
// with the row-count preamble, or the literal "- none" empty-section fill).
export function parseScopeSegments(text) {
  if (typeof text !== 'string') return null;
  if (!/^\d+\s+rows\s+·\s/.test(text)) return null;
  const segments = text.split(' · ').map((s) => s.trim()).filter(Boolean);
  return segments.length ? segments : null;
}

// Block parsing lives in utils/markdownBlocks.js (pure, unit-tested). Everything below renders its
// output as React elements: text is only ever a child, never HTML.

// Plain elements with inline `style` so the App.css tokens apply and the output is checkable in a
// static render.
const TABLE_STYLE = { borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem', color: 'var(--ink)' };
const CELL_STYLE = { border: '1px solid var(--line)', padding: '4px 8px', verticalAlign: 'top' };
const TH_STYLE = { ...CELL_STYLE, background: 'var(--card)', fontWeight: 700 };
const PRE_STYLE = {
  margin: 0, padding: '8px 10px', overflowX: 'auto', background: 'var(--surface)', color: 'var(--ink)',
  border: '1px solid var(--line)', borderRadius: 4, fontSize: '0.85rem',
};

function MarkdownTable({ block, keyPrefix, onRowRef }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={TABLE_STYLE}>
        <thead>
          <tr>
            {block.header.map((h, j) => (
              <th key={j} style={{ ...TH_STYLE, textAlign: block.align[j] || 'left' }}>
                {renderInline(h, `${keyPrefix}-th-${j}`, onRowRef)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, r) => (
            <tr key={r}>
              {row.map((c, j) => (
                <td key={j} style={{ ...CELL_STYLE, textAlign: block.align[j] || 'left' }}>
                  {renderInline(c, `${keyPrefix}-td-${r}-${j}`, onRowRef)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

// The Scope line rendered as a compact row of outlined chips, one per
// " · "-delimited segment (see parseScopeSegments). Falls back to the caller
// rendering plain text when the section doesn't match the deterministic shape.
function ScopeChipRow({ segments, keyPrefix }) {
  return (
    <Stack key={keyPrefix} direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      {segments.map((seg, j) => (
        <Chip
          key={`${keyPrefix}-${j}`}
          size="small"
          variant="outlined"
          label={seg}
          sx={{ height: 20, fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace' }}
        />
      ))}
    </Stack>
  );
}

// "### Missing / not verifiable" gets the amber/warning treatment — these are
// the deterministic gaps computed by conformance/answerFormat.js's
// buildMissing, worth visually flagging even when the model's prose above
// reads confidently.
function MissingList({ items, keyPrefix, onRowRef }) {
  return (
    <Box
      key={keyPrefix}
      component="ul"
      sx={{
        m: 0, pl: 2.5, py: 0.5,
        borderLeft: '3px solid', borderColor: 'warning.main',
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,167,38,0.08)' : 'rgba(255,167,38,0.10)'),
      }}
    >
      {items.map((item, j) => (
        <Typography key={j} component="li" variant="body2" color="warning.main" sx={{ mb: 0.25 }}>
          {renderInline(item, `missing-${keyPrefix}-${j}`, onRowRef)}
        </Typography>
      ))}
    </Box>
  );
}

// `onRowRef(rowId, label)` — optional. When provided, `[#rowId ...]` citations
// render as clickable chips that call it on click (see ChecksPage's
// handleRowRefClick, which resolves rowId against the rows array posted with
// that turn and jumps InspectraNetworkView to the matching row). Omitted ->
// citations still render, just as plain non-interactive text (e.g. nowhere
// else in the app that reuses this component needs to wire row lookup).
export default function AnswerMarkdown({ text, onRowRef }) {
  const blocks = useMemo(() => parseMarkdownBlocks(text), [text]);
  if (!blocks.length) {
    return <Typography variant="body2" color="text.secondary">(no answer)</Typography>;
  }
  let currentSection = null; // last h3 heading text seen, for heading-keyed styling below
  return (
    <Stack spacing={1}>
      {blocks.map((b, i) => {
        if (b.type === 'table') {
          return <MarkdownTable key={i} block={b} keyPrefix={`tbl-${i}`} onRowRef={onRowRef} />;
        }
        if (b.type === 'code') {
          return (
            <pre key={i} style={PRE_STYLE}>
              <code style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{b.text}</code>
            </pre>
          );
        }
        if (b.type === 'h1') {
          currentSection = null;
          return (
            <Box key={i} sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.25, py: 0.25 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                {renderInline(b.text, `h1-${i}`, onRowRef)}
              </Typography>
            </Box>
          );
        }
        if (b.type === 'h2') {
          currentSection = null;
          return (
            <Box key={i} sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.25, py: 0.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                {renderInline(b.text, `h2-${i}`, onRowRef)}
              </Typography>
            </Box>
          );
        }
        if (b.type === 'h3') {
          currentSection = b.text;
          return (
            <Typography
              key={i}
              variant="caption"
              sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mt: 1 }}
            >
              {renderInline(b.text, `h3-${i}`, onRowRef)}
            </Typography>
          );
        }
        if (currentSection === MISSING_HEADING && (b.type === 'ul' || b.type === 'ol')) {
          return <MissingList key={i} items={b.items} keyPrefix={i} onRowRef={onRowRef} />;
        }
        if (currentSection === SCOPE_HEADING && b.type === 'p') {
          const segments = parseScopeSegments(b.text);
          if (segments) return <ScopeChipRow key={i} segments={segments} keyPrefix={i} />;
          // falls through to the plain-paragraph rendering below
        }
        if (b.type === 'ul' || b.type === 'ol') {
          return (
            <Box key={i} component={b.type === 'ul' ? 'ul' : 'ol'} sx={{ m: 0, pl: 2.5 }}>
              {b.items.map((item, j) => (
                <Typography key={j} component="li" variant="body2" sx={{ mb: 0.25 }}>
                  {renderInline(item, `${b.type}-${i}-${j}`, onRowRef)}
                </Typography>
              ))}
            </Box>
          );
        }
        return (
          <Typography key={i} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {renderInline(b.text, `p-${i}`, onRowRef)}
          </Typography>
        );
      })}
    </Stack>
  );
}
